/**
 * Mantra fingerprint integration.
 *
 * Scanner (local MFScan service, REACT_APP_MFSCAN_URL):
 *   POST connecteddevicelist  → device presence
 *   POST checkdevice          → device name + status
 *   POST initdevice           → required by the SDK before capture
 *   POST capture              → BitmapData
 *
 * Fingerprint API (REACT_APP_FINGERPRINT_API_URL):
 *   POST   api/Fingerprint/enroll
 *   POST   api/Fingerprint/search
 *   POST   api/Fingerprint/verify
 *   DELETE api/Fingerprint/delete
 *
 * Captured BitmapData is sent as TemplateData to the fingerprint API.
 */

const DEVICE_METHODS = {
  connectedDeviceList: "connecteddevicelist",
  checkDevice: "checkdevice",
  initDevice: "initdevice",
  capture: "capture",
};

/** MFS500 SDK code returned when capture runs before initdevice. */
const DEVICE_NOT_INITIALIZED_CODE = -2025;

const FINGERPRINT_PATHS = {
  enroll: "api/Fingerprint/enroll",
  search: "api/Fingerprint/search",
  verify: "api/Fingerprint/verify",
  delete: "api/Fingerprint/delete",
};

const CAPTURE_QUALITY = 60;
/** Device capture window (seconds). Shown as the modal countdown. */
export const CAPTURE_TIMEOUT_SEC = 20;
const DEVICE_TIMEOUT_MS = CAPTURE_TIMEOUT_SEC * 1000 + 20000;
const API_TIMEOUT_MS = 30000;

const trimTrailingSlash = (url) => String(url || "").replace(/\/+$/, "");

export const getScannerBaseUrl = () => {
  const raw = String(process.env.REACT_APP_MFSCAN_URL || "").trim();
  if (!raw) {
    throw new Error(
      "Missing REACT_APP_MFSCAN_URL. Set the MFScan service URL in the .env file."
    );
  }
  return `${trimTrailingSlash(raw)}/`;
};

export const getFingerprintApiBaseUrl = () => {
  const raw = String(process.env.REACT_APP_FINGERPRINT_API_URL || "").trim();
  if (!raw) {
    throw new Error(
      "Missing REACT_APP_FINGERPRINT_API_URL. Set the fingerprint API base URL in the .env file."
    );
  }
  return `${trimTrailingSlash(raw)}/`;
};

export const getFingerprintGroup = () =>
  String(process.env.REACT_APP_FINGERPRINT_GROUP || "").trim();

export const getFingerprintInstallerDownloads = () => {
  const driverUrl = String(process.env.REACT_APP_MORFIN_DRIVER_URL || "").trim();
  const clientUrl = String(process.env.REACT_APP_MFSCAN_CLIENT_URL || "").trim();
  const items = [];
  if (driverUrl) {
    items.push({
      label: "Download fingerprint driver",
      url: driverUrl,
    });
  }
  if (clientUrl) {
    items.push({
      label: "Download scanner client",
      url: clientUrl,
    });
  }
  return items;
};

const getScannerClientKey = () =>
  String(process.env.REACT_APP_MFSCAN_CLIENT_KEY || "").trim();

const parseResponseBody = async (response) => {
  const text = await response.text();
  if (!text.trim()) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const readField = (payload, keys) => {
  if (!payload || typeof payload !== "object") return undefined;
  for (const key of keys) {
    if (payload[key] != null && payload[key] !== "") return payload[key];
  }
  return undefined;
};

const getPayloadMessage = (payload) => {
  const message = readField(payload, [
    "ErrorDescription",
    "errorDescription",
    "Message",
    "message",
    "Error",
    "error",
    "rawMessage",
  ]);
  return message != null ? String(message).trim() : "";
};

/**
 * @param {unknown} err
 * @returns {{ kind: 'timeout' | 'duplicate' | 'error'; message: string }}
 */
export const classifyFingerprintScanError = (err) => {
  const message = String(err?.message || err || "").trim();
  const lower = message.toLowerCase();
  if (/time\s*-?\s*out|timed out/i.test(lower)) {
    return {
      kind: "timeout",
      message: "Scan timed out. Place your finger on the scanner and tap Rescan.",
    };
  }
  if (/duplicate/i.test(lower)) {
    return {
      kind: "duplicate",
      message:
        "This fingerprint is already enrolled. Rescan, or use a different finger.",
    };
  }
  return {
    kind: "error",
    message: message || "Fingerprint scan failed. Please rescan.",
  };
};

const sendJson = async ({
  url,
  method = "POST",
  payload = null,
  timeoutMs,
  unreachableMessage,
}) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json; charset=utf-8" },
      body: payload == null ? undefined : JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error("Fingerprint request timed out. Please try again.");
    }
    throw new Error(unreachableMessage);
  } finally {
    clearTimeout(timer);
  }

  const body = await parseResponseBody(response);
  if (!response.ok) {
    throw new Error(
      getPayloadMessage(body) || `Request failed (HTTP ${response.status}).`
    );
  }
  return body;
};

/* ---------- Scanner (local MFScan service) ---------- */

const scannerUnreachableMessage = () =>
  `Could not reach the fingerprint scanner service. Open ${getScannerBaseUrl()} in this browser and accept the certificate.`;

const readDeviceErrorCode = (payload) => {
  const code = parseInt(readField(payload, ["ErrorCode", "errorCode"]), 10);
  return Number.isFinite(code) ? code : null;
};

const isDeviceSuccess = (payload) => readDeviceErrorCode(payload) === 0;

const parseConnectedDevices = (payload) => {
  const description = String(getPayloadMessage(payload));
  const match = description.match(/Connected Device\s*:?\s*(.+)$/i);
  if (!match) return [];
  return match[1]
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const scannerPost = async (scannerMethod, payload = null) =>
  sendJson({
    url: `${getScannerBaseUrl()}${scannerMethod}`,
    payload,
    timeoutMs: DEVICE_TIMEOUT_MS,
    unreachableMessage: scannerUnreachableMessage(),
  });

/**
 * @typedef {{
 *   deviceConnected: boolean;
 *   deviceModel: string;
 *   statusMessage: string;
 * }} DeviceStatus
 */

/**
 * Read scanner status once (connecteddevicelist, then checkdevice).
 * @returns {Promise<DeviceStatus>}
 */
export const checkDeviceStatus = async () => {
  try {
    const listPayload = await scannerPost(DEVICE_METHODS.connectedDeviceList);

    if (!isDeviceSuccess(listPayload)) {
      return {
        deviceConnected: false,
        deviceModel: "",
        statusMessage:
          getPayloadMessage(listPayload) ||
          "Fingerprint scanner not connected. Plug in the device and check again.",
      };
    }

    const devices = parseConnectedDevices(listPayload);
    if (!devices.length) {
      return {
        deviceConnected: false,
        deviceModel: "",
        statusMessage:
          "Fingerprint scanner not connected. Plug in the device via USB, then check again.",
      };
    }

    const deviceModel = devices[0];
    const checkPayload = await scannerPost(DEVICE_METHODS.checkDevice, {
      ConnectedDvc: deviceModel,
    });

    // "Device not initialized" only means initdevice has not run yet, which
    // happens as part of the capture step.
    const notInitialised =
      readDeviceErrorCode(checkPayload) === DEVICE_NOT_INITIALIZED_CODE;

    if (!isDeviceSuccess(checkPayload) && !notInitialised) {
      return {
        deviceConnected: false,
        deviceModel,
        statusMessage:
          getPayloadMessage(checkPayload) ||
          `${deviceModel} detected but not responding. Reconnect the USB cable.`,
      };
    }

    return {
      deviceConnected: true,
      deviceModel,
      statusMessage: `${deviceModel} connected. Ready to scan.`,
    };
  } catch (err) {
    return {
      deviceConnected: false,
      deviceModel: "",
      statusMessage: err?.message?.trim() || scannerUnreachableMessage(),
    };
  }
};

/**
 * @typedef {{
 *   bitmapData: string;
 *   templateData: string;
 *   quality: number | null;
 *   nfiq: number | null;
 * }} CaptureResult
 */

const resolveDeviceModel = async () => {
  const listPayload = await scannerPost(DEVICE_METHODS.connectedDeviceList);
  if (!isDeviceSuccess(listPayload)) {
    throw new Error(
      getPayloadMessage(listPayload) || "No fingerprint scanner connected."
    );
  }
  const [deviceModel] = parseConnectedDevices(listPayload);
  if (!deviceModel) {
    throw new Error("No fingerprint scanner connected.");
  }
  return deviceModel;
};

const initDevice = async (deviceModel) => {
  const payload = await scannerPost(DEVICE_METHODS.initDevice, {
    ConnectedDvc: deviceModel,
    ClientKey: getScannerClientKey(),
  });

  const message = getPayloadMessage(payload);
  if (isDeviceSuccess(payload) || /already/i.test(message)) return;

  throw new Error(
    message || `Could not initialize ${deviceModel} on the scanner service.`
  );
};

const requestCapture = () =>
  scannerPost(DEVICE_METHODS.capture, {
    Quality: CAPTURE_QUALITY,
    TimeOut: CAPTURE_TIMEOUT_SEC,
  });

/**
 * Capture one fingerprint. Initializes the device first, since the SDK rejects
 * capture with "Device not initialized." (-2025) otherwise.
 * @param {{ deviceModel?: string }} [options]
 * @returns {Promise<CaptureResult>}
 */
export const captureFingerprint = async ({ deviceModel } = {}) => {
  const model = String(deviceModel || "").trim() || (await resolveDeviceModel());

  await initDevice(model);
  let payload = await requestCapture();

  // The service drops initialization when another app grabs the device.
  if (readDeviceErrorCode(payload) === DEVICE_NOT_INITIALIZED_CODE) {
    await initDevice(model);
    payload = await requestCapture();
  }

  if (!isDeviceSuccess(payload)) {
    throw new Error(
      getPayloadMessage(payload) || "Fingerprint capture failed. Try again."
    );
  }

  const bitmapData = String(
    readField(payload, ["BitmapData", "bitmapData"]) || ""
  ).trim();
  const templateData = String(
    readField(payload, [
      "ISOTemplate",
      "IsoTemplate",
      "ISOTemplateData",
      "TemplateData",
      "templateData",
      "AnsiTemplate",
      "ANSITemplate",
    ]) || ""
  ).trim();
  if (!bitmapData && !templateData) {
    throw new Error("Capture succeeded but no fingerprint template was returned.");
  }

  const qualityRaw = readField(payload, ["Quality", "quality"]);
  const nfiqRaw = readField(payload, ["Nfiq", "nfiq"]);
  const quality = qualityRaw != null ? Number(qualityRaw) : NaN;
  const nfiq = nfiqRaw != null ? Number(nfiqRaw) : NaN;

  return {
    bitmapData,
    templateData: templateData || bitmapData,
    quality: Number.isFinite(quality) ? quality : null,
    nfiq: Number.isFinite(nfiq) ? nfiq : null,
  };
};

/* ---------- Fingerprint API ---------- */

const apiUnreachableMessage =
  "Could not reach the fingerprint API. Check REACT_APP_FINGERPRINT_API_URL in .env and that the service allows this site (CORS).";

const isApiSuccess = (payload) => {
  if (payload == null) return true;
  if (typeof payload === "boolean") return payload;

  const flag = readField(payload, ["Status", "status", "Success", "success"]);
  if (typeof flag === "boolean") return flag;
  if (typeof flag === "string" && /^(false|fail|failed|error)$/i.test(flag)) {
    return false;
  }

  const code = readField(payload, ["code", "Code", "ErrorCode", "errorCode"]);
  if (code != null && code !== "") {
    const n = parseInt(code, 10);
    if (Number.isFinite(n)) {
      return n === 0 || n === 200 || n === 201;
    }
  }

  const message = getPayloadMessage(payload);
  if (/duplicate biometric/i.test(message)) return false;

  return true;
};

const apiRequest = async (pathKey, method, payload) => {
  const result = await sendJson({
    url: `${getFingerprintApiBaseUrl()}${FINGERPRINT_PATHS[pathKey]}`,
    method,
    payload,
    timeoutMs: API_TIMEOUT_MS,
    unreachableMessage: apiUnreachableMessage,
  });
  if (!isApiSuccess(result)) {
    throw new Error(
      getPayloadMessage(result) || "Fingerprint request was rejected."
    );
  }
  return result;
};

const pickNested = (payload, keys) => {
  const direct = readField(payload, keys);
  if (direct != null) return String(direct).trim();

  const nested = readField(payload, ["Data", "data", "Result", "result"]);
  if (nested && typeof nested === "object") {
    const inner = readField(nested, keys);
    if (inner != null) return String(inner).trim();
  }
  return "";
};

const MATCH_FLAG_KEYS = [
  "Matched",
  "matched",
  "IsMatch",
  "isMatch",
  "IsMatched",
  "isMatched",
  "Verified",
  "verified",
  "IsVerified",
  "isVerified",
];

const SCORE_KEYS = ["Score", "score", "MatchScore", "matchScore"];
/** Hosted verify API score: 0 to 400. Match if score >= min (default 150). */
export const MAX_VERIFY_MATCH_SCORE = 400;
const DEFAULT_MIN_VERIFY_MATCH_SCORE = 150;

export const getMinVerifyMatchScore = () => {
  const raw = Number(process.env.REACT_APP_FINGERPRINT_MIN_MATCH_SCORE);
  if (Number.isFinite(raw) && raw >= 0) return raw;
  return DEFAULT_MIN_VERIFY_MATCH_SCORE;
};

/** Convert a verify score (0–400) to a 0–100 match percentage for the UI. */
export const formatMatchPercent = (score) => {
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, Math.round((n / MAX_VERIFY_MATCH_SCORE) * 100)));
};

const readFromPayloadOrData = (payload, keys) => {
  const direct = readField(payload, keys);
  if (direct != null && direct !== "") return direct;
  const nested = readField(payload, ["Data", "data", "Result", "result"]);
  if (nested && typeof nested === "object") {
    const inner = readField(nested, keys);
    if (inner != null && inner !== "") return inner;
  }
  return undefined;
};

const parseMatchFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === "string") {
    const text = value.trim();
    if (/^(true|yes|1|match(ed)?|verified)$/i.test(text)) return true;
    if (/^(false|no|0|unmatched|not[_\s-]*match(ed)?)$/i.test(text)) return false;
  }
  return null;
};

const inferMatchFromMessage = (message) => {
  const text = String(message || "").trim();
  if (!text) return null;
  if (/not\s*match|no\s*match|unmatched|mismatch|different/i.test(text)) {
    return false;
  }
  if (/^verified$|match(ed)?\s*(successfully)?$/i.test(text)) return true;
  return null;
};

const toApiResult = (payload) => {
  const scoreRaw = readFromPayloadOrData(payload, SCORE_KEYS);
  const score = scoreRaw != null ? Number(scoreRaw) : NaN;

  let matched = parseMatchFlag(readFromPayloadOrData(payload, MATCH_FLAG_KEYS));
  if (matched == null) {
    matched = inferMatchFromMessage(getPayloadMessage(payload));
  }
  if (matched == null && Number.isFinite(score)) {
    matched = score >= getMinVerifyMatchScore();
  }

  return {
    templateData: pickNested(payload, [
      "TemplateData",
      "templateData",
      "Template",
      "template",
    ]),
    personId: pickNested(payload, [
      "PersonId",
      "personId",
      "ExternalId",
      "externalId",
    ]),
    score: Number.isFinite(score) ? score : null,
    matched,
    payload,
  };
};

/**
 * Register a fingerprint template for a person.
 * @param {{ personId: string; templateData: string; group?: string }} params
 */
export const enrollFingerprint = async ({ personId, templateData, group }) => {
  const payload = await apiRequest("enroll", "POST", {
    TemplateData: String(templateData || ""),
    PersonId: String(personId || "").trim(),
    Group: String(group ?? getFingerprintGroup()),
  });
  return toApiResult(payload);
};

/**
 * Identify a person from a fingerprint template (one-to-many).
 * @param {{ templateData: string; personId?: string; group?: string }} params
 */
export const searchFingerprint = async ({
  templateData,
  personId = "",
  group,
}) => {
  const payload = await apiRequest("search", "POST", {
    TemplateData: String(templateData || ""),
    PersonId: String(personId || "").trim(),
    Group: String(group ?? getFingerprintGroup()),
  });
  return toApiResult(payload);
};

const isLikelyImageTemplate = (value) => {
  const text = String(value || "").trim();
  return /^data:image\//i.test(text) || /^Qk/i.test(text);
};

/**
 * Live scan value for POST /api/Fingerprint/verify SourceTemplate.
 * Uses the ISO/ANSI template when the enrolled target is a template,
 * and BitmapData when the enrolled target is an image.
 */
export const pickVerifySourceTemplate = (captured, targetTemplate) => {
  const target = String(targetTemplate || "").trim();
  const liveTemplate = String(captured?.templateData || "").trim();
  const liveBitmap = String(captured?.bitmapData || "").trim();
  if (isLikelyImageTemplate(target)) return liveBitmap || liveTemplate;
  return liveTemplate || liveBitmap;
};

const isNoMatchMessage = (message) =>
  /no\s*match|not\s*match|unmatched|mismatch/i.test(String(message || ""));

const parseVerifyMatch = (payload) => {
  const message = getPayloadMessage(payload);
  if (isNoMatchMessage(message)) return false;

  const scoreRaw = readFromPayloadOrData(payload, SCORE_KEYS);
  const score = scoreRaw != null ? Number(scoreRaw) : NaN;
  if (Number.isFinite(score)) return score >= getMinVerifyMatchScore();

  const flag = parseMatchFlag(readFromPayloadOrData(payload, MATCH_FLAG_KEYS));
  if (flag != null) return flag;

  if (/^\s*match\s*$/i.test(message) || /\bmatched\b/i.test(message)) {
    return true;
  }

  return false;
};

/**
 * One-to-one verify: current scan vs enrolled template.
 * POST { SourceTemplate, TargetTemplate }
 * @param {{ sourceTemplate: string; targetTemplate: string }} params
 */
export const verifyFingerprint = async ({ sourceTemplate, targetTemplate }) => {
  const source = String(sourceTemplate || "").trim();
  const target = String(targetTemplate || "").trim();
  if (!source) {
    throw new Error("Live fingerprint scan is missing. Please rescan.");
  }
  if (!target) {
    throw new Error(
      "Previously enrolled fingerprint template is missing for this driver."
    );
  }
  if (source === target) {
    throw new Error(
      "Live scan and enrolled template are identical. Recapture the live finger."
    );
  }

  let payload;
  try {
    payload = await sendJson({
      url: `${getFingerprintApiBaseUrl()}${FINGERPRINT_PATHS.verify}`,
      method: "POST",
      payload: {
        SourceTemplate: source,
        TargetTemplate: target,
      },
      timeoutMs: API_TIMEOUT_MS,
      unreachableMessage: apiUnreachableMessage,
    });
  } catch (err) {
    if (isNoMatchMessage(err?.message)) {
      return { templateData: "", personId: "", score: null, matched: false, payload: null };
    }
    throw err;
  }

  const result = toApiResult(payload);
  return {
    ...result,
    matched: parseVerifyMatch(payload) === true,
  };
};

/**
 * Remove a person's enrolled fingerprint data.
 * @param {{ externalId: string }} params
 */
export const deleteFingerprint = async ({ externalId }) => {
  const id = String(externalId || "").trim();
  if (!id) {
    throw new Error("ExternalId is required to delete a fingerprint.");
  }
  try {
    const payload = await apiRequest("delete", "DELETE", {
      ExternalId: id,
    });
    return toApiResult(payload);
  } catch (err) {
    const message = String(err?.message || "");
    if (/not\s*found|does not exist|no person|unknown person/i.test(message)) {
      return {
        templateData: "",
        personId: "",
        score: null,
        matched: null,
        payload: null,
      };
    }
    throw err;
  }
};

/** Build a data URL for the captured BitmapData preview. */
export const fingerprintImageSrc = (imageData) => {
  const raw = String(imageData || "").trim();
  if (!raw || raw.startsWith("<")) return null;
  if (raw.startsWith("data:image/")) return raw;
  return `data:image/bmp;base64,${raw}`;
};
